import React, { useId, useState } from 'react';
import { ListPlus, Search } from 'lucide-react';
import Input from '../../../ui/Input';
import '../EditorChrome.css';

/**
 * Schwebendes Hinzufügen-Feld unten rechts — ersetzt die frühere Sticky-Zeile
 * (Spec 2026-08-07, Abschnitt „Das schwebende Hinzufügen-Feld"). Immer offen,
 * Autocomplete öffnet nach oben.
 *
 * Bildschirmleser: WAI-ARIA-Combobox — das Feld ist die `combobox`, die Liste
 * die `listbox`, der per Pfeiltaste hervorgehobene Eintrag steht in
 * `aria-activedescendant`. Der Fokus bleibt dabei immer im Feld.
 *
 * Unter der Liste steht „Erweiterte Suche…" (`onOpenCatalogSearch`) — ein Knopf, keine
 * Option: er gehört nicht zur Trefferliste und darf ihre `listbox`-Semantik nicht
 * verwässern. Er erscheint, sobald das Feld den Fokus hat, also auch ohne Eingabe und
 * ohne Treffer — genau dann sucht man einen anderen Weg als den Namen.
 *
 * Daneben steht ein Lupen-Knopf mit derselben Aktion — sichtbar, ohne dass man ins Feld
 * klickt; wer per Ziehen arbeitet, sieht den Weg in die Suche sonst nie.
 *
 * `hidden` macht den Dock unsichtbar, ohne ihn auszuhängen: Eingabetext und Fokus bleiben
 * erhalten (nötig, solange die Leiste des externen Karten-Zugs darüber liegt).
 *
 * `rightInset` (px) rückt den Dock vom Fensterrand nach links — er sitzt am rechten Rand
 * der Playmat, nicht des Fensters, und wandert mit, wenn daneben der Katalog aufgeht.
 *
 * `searchHidden` lässt Feld und Lupe weg und zeigt nur den Kategorie-Knopf: Bei offenem
 * Katalog übernimmt dessen Suchfeld das Hinzufügen, das Anlegen einer Kategorie hat dort
 * aber keinen Ersatz. Ohne Kategorie-Recht bleibt dann nichts übrig, der Dock rendert nicht.
 */
export default function FloatingAddDock({
  addCardInputRef, addCardSearchTerm, onAddCardSearchChange, onAddCardKeyDown,
  onAddCardBlur, showAutocomplete, autocompleteResults, highlightedIndex,
  onAutocompleteSelect, onOpenAddCategory,
  canAddCategory, onOpenCatalogSearch, hidden = false, searchHidden = false,
  rightInset = 0,
}) {
  const [focused, setFocused] = useState(false);
  const listboxId = useId();
  const optionId = (index) => `${listboxId}-option-${index}`;
  const listOpen = showAutocomplete && autocompleteResults.length > 0;
  const activeOptionId = listOpen && highlightedIndex >= 0 && highlightedIndex < autocompleteResults.length
    ? optionId(highlightedIndex)
    : undefined;
  const katalogEinstieg = typeof onOpenCatalogSearch === 'function';
  const popupOpen = listOpen || (katalogEinstieg && focused);

  if (searchHidden && !canAddCategory) return null;

  return (
    <div
      className={`de-add-dock${hidden ? ' de-add-dock--hidden' : ''}`}
      style={rightInset > 0 ? { '--de-add-dock-inset': `${rightInset}px` } : undefined}
    >
      {!searchHidden && <span className="de-add-dock__input">
        <Input
          ref={addCardInputRef}
          value={addCardSearchTerm}
          onChange={onAddCardSearchChange}
          onKeyDown={onAddCardKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={(event) => { setFocused(false); onAddCardBlur?.(event); }}
          placeholder="Karte hinzufügen…"
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={listOpen ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
        />
        {popupOpen && (
          <div className="de-add-dock__popup">
            {listOpen && (
              <ul
                id={listboxId}
                role="listbox"
                aria-label="Kartenvorschläge"
                className="deck-editor-autocomplete"
              >
                {autocompleteResults.map((suggestion, index) => (
                  <li
                    key={suggestion.scryfallId || suggestion.name}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    className={`deck-editor-autocomplete-item${index === highlightedIndex ? ' highlighted' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); onAutocompleteSelect(suggestion); }}
                  >
                    {suggestion.printedName ? (
                      <>
                        <span className="deck-editor-autocomplete-item__primary">{suggestion.printedName}</span>
                        <span className="deck-editor-autocomplete-item__secondary">{suggestion.name}</span>
                      </>
                    ) : suggestion.name}
                  </li>
                ))}
              </ul>
            )}
            {katalogEinstieg && (
              <button
                type="button"
                className="de-add-dock__more"
                // Ohne dieses Abfangen verlöre das Feld den Fokus, bevor der Klick ankommt —
                // die Fußzeile wäre dann schon aus dem DOM.
                onMouseDown={(e) => e.preventDefault()}
                onClick={onOpenCatalogSearch}
              >
                Erweiterte Suche…
              </button>
            )}
          </div>
        )}
      </span>}
      {katalogEinstieg && !searchHidden && (
        <button
          type="button"
          className="btn-icon de-add-dock__search"
          title="Erweiterte Suche"
          aria-label="Erweiterte Suche"
          onClick={onOpenCatalogSearch}
        >
          <Search size={16} aria-hidden />
        </button>
      )}
      {canAddCategory && (
        <button type="button" className="btn-secondary" onClick={onOpenAddCategory}>
          <ListPlus size={16} aria-hidden /> Kategorie
        </button>
      )}
    </div>
  );
}
